// src/components/viewer/ComponentSearchResultsBanner.tsx
import React, { useMemo } from "react";
import { ActionIcon, Alert, Group, Text } from "@mantine/core";
import { FaInfoCircle } from "react-icons/fa";
import { FaXmark } from "react-icons/fa6";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectMcbimItemHitSetByFreeTextSearch, selectSpacesWithMcbimItemsHitsByFreeTextSearch } from "../ViewerRedux/viewerSelectors";
import { selectActiveSearch, selectComponentSearchText, clearFreeTextSearchInRedux } from "../ViewerRedux/viewerSlice";


export const ComponentSearchResultsBanner: React.FC = () => {
  const dispatch = useAppDispatch();

  const activeSearch = useAppSelector(selectActiveSearch);
  const query = useAppSelector(selectComponentSearchText);

  const hitSet = useAppSelector(selectMcbimItemHitSetByFreeTextSearch);
  const spacesWithHits = useAppSelector(selectSpacesWithMcbimItemsHitsByFreeTextSearch);

  const q = (query ?? "").trim();
  const isActive = activeSearch === "COMPONENT" && q.length > 0;

  const componentsCount = hitSet?.size ?? 0;
  const roomsCount = spacesWithHits?.length ?? 0;

  const title = useMemo(() => {
    if (!isActive) return "";
    if (hitSet == null) return "";
    if (componentsCount === 0) return "Ingen komponenter matcher søket";

    // "56 komponenter i 34 rom"
    const compTxt = componentsCount === 1 ? "1 komponent" : `${componentsCount} komponenter`;
    const roomTxt = roomsCount === 1 ? "1 rom" : `${roomsCount} rom`;
    return `${compTxt} i ${roomTxt} matchet søket`;
  }, [isActive, hitSet, componentsCount, roomsCount]);

  if (!isActive) return null;
  if (hitSet == null) return null;

  const color = componentsCount === 0 ? "gray" : "teal";

  return (
    <Alert
      variant="light"
      color={color}
      radius="xl"
      icon={<FaInfoCircle size={16} />}
      styles={{
        root: {
          paddingTop: 10,
          paddingBottom: 10,
          paddingLeft: 14,
          paddingRight: 10,
          backgroundColor:
            componentsCount === 0 ? "var(--mantine-color-gray-1)" : "var(--mantine-color-teal-1)",
          border:
            componentsCount === 0
              ? "1px solid var(--mantine-color-gray-4)"
              : "1px solid var(--mantine-color-teal-4)",
          boxShadow: "0 4px 18px rgba(0,0,0,0.18)",
        },
      }}
    >
      <Group justify="space-between" align="center" wrap="nowrap">
        <div>
          <Text size="sm" fw={600} c="black">
            {title}
          </Text>
          <Text size="xs" c="dimmed">
            Søkeord:{" "}
            <Text span fw={600}>
              {q}
            </Text>
          </Text>
        </div>

        <ActionIcon
          variant="subtle"
          color={color}
          radius="xl"
          aria-label="Tøm søk"
          onClick={() => dispatch(clearFreeTextSearchInRedux())}
        >
          <FaXmark size={14} />
        </ActionIcon>
      </Group>
    </Alert>
  );
};